import React, { useEffect } from "react";
import { X } from "lucide-react";

/**
 * Shared shell for the organisation "choose how to add" dialogs (Add a
 * Project, Add a Client): icon tile + title + subtitle header, a tinted body,
 * and a footer carrying a hint on the left and Cancel / primary on the right.
 *
 * Purely presentational — each dialog keeps its own state and callbacks.
 */
export const ChoiceDialog = ({
  icon,
  title,
  subtitle,
  onClose,
  footerHint,
  primaryLabel,
  onPrimary,
  primaryDisabled,
  children,
}) => {
  // Esc dismisses, as the footer hint promises.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <>
      <div
        className="fixed inset-0 z-50 bg-[#0F172A]/40 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        className="fixed left-1/2 top-1/2 z-50 w-[640px] max-w-[calc(100vw-32px)] max-h-[90vh] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[18px] bg-white shadow-[0_24px_60px_rgba(15,23,42,0.25)] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-start gap-4 border-b border-[#EEF1F6] px-6 pb-5 pt-6 sm:px-8">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[12px] border border-[#E4E8F2] bg-[#F5F6FF] text-[#4F46E5]">
            {icon}
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-[20px] font-semibold text-[#111827]">{title}</h2>
            {subtitle && (
              <p className="mt-1 text-[14px] leading-[22px] text-[#667085]">
                {subtitle}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 rounded-md p-1 text-[#98A2B3] hover:bg-[#F2F4F7] hover:text-[#475467]"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto bg-[#FBFCFE] px-6 py-6 sm:px-8">
          {children}
        </div>

        {/* Footer — only when there is a primary action to offer */}
        {primaryLabel && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#EEF1F6] bg-white px-6 py-4 sm:px-8">
            <div className="text-[12px] text-[#98A2B3]">{footerHint}</div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="h-10 rounded-[10px] px-4 text-[14px] font-medium text-[#344054] hover:bg-[#F2F4F7]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onPrimary}
                disabled={primaryDisabled}
                className="flex h-10 items-center gap-2 rounded-[10px] bg-[#4F46E5] px-5 text-[14px] font-semibold text-white shadow-[0_1px_2px_rgba(16,24,40,0.1)] hover:bg-[#4338CA] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {primaryLabel}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

/**
 * One selectable row inside a ChoiceDialog: radio, icon tile, title with an
 * optional tag, and a description. Selected state gets the indigo ring.
 */
export const ChoiceOption = ({
  selected,
  onSelect,
  icon,
  title,
  tag,
  description,
}) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    onClick={onSelect}
    className={`flex w-full items-start gap-3 rounded-[12px] border bg-white p-3 text-left transition-colors sm:gap-4 ${
      selected
        ? "border-[#4F46E5] ring-2 ring-[#4F46E5]/15"
        : "border-[#E4E7EC] hover:border-[#C7CBF5]"
    }`}
  >
    <span
      className={`mt-2.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border ${
        selected ? "border-[#4F46E5]" : "border-[#D0D5DD]"
      }`}
    >
      {selected && <span className="h-2.5 w-2.5 rounded-full bg-[#4F46E5]" />}
    </span>
    <span
      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] ${
        selected
          ? "bg-[#4F46E5] text-white"
          : "border border-[#E4E7EC] bg-[#F9FAFB] text-[#475467]"
      }`}
    >
      {icon}
    </span>
    <span className="min-w-0 flex-1">
      <span className="flex items-start justify-between gap-2">
        <span className="text-[15px] font-semibold text-[#111827]">{title}</span>
        {tag && (
          <span
            className={`shrink-0 rounded-[6px] px-2 py-[2px] text-[11px] font-semibold ${
              selected
                ? "bg-[#EEF0FF] text-[#4F46E5]"
                : "bg-[#F2F4F7] text-[#475467]"
            }`}
          >
            {tag}
          </span>
        )}
      </span>
      <span className="mt-1 block text-[13px] leading-[20px] text-[#667085]">
        {description}
      </span>
    </span>
  </button>
);

export default ChoiceDialog;
